import {createSelector} from '@reduxjs/toolkit'

import {head} from '@shared/helpers/array'
import {mapValueKeys, reduceValueKeys} from '@shared/helpers/object'

import {DiscountPolicy, PickUpLocation} from '@web/core/constants'
import {getShoppingSubscriptionOrderTotal} from '@web/helpers/policies'
import {orderTotalSelector} from '@web/selectors/orderTotal'
import {agreementSelector} from '@web/slices/agreement/slice'
import {deliveryInfoSelector} from '@web/slices/deliveryInfo/slice'
import {
    orderSheetCouponSelector,
    OrderSheetCouponState,
    productItemDiscountInfoSelector,
    subscriptionDiscountPolicySelector,
} from '@web/slices/orderSheetCoupon/slice'
import {orderSheetProductInfoSelector} from '@web/slices/orderSheetProducts/slice'
import {shoppingSubscriptionSelector} from '@web/slices/shoppingSubscription/slice'

export const startSubscriptionRequestBodySelector = createSelector(
    agreementSelector,
    deliveryInfoSelector,
    orderSheetCouponSelector,
    shoppingSubscriptionSelector,
    subscriptionDiscountPolicySelector,
    orderTotalSelector,
    orderSheetProductInfoSelector,
    productItemDiscountInfoSelector,
    (
        {selected: {shoppingSubscription: agreed}},
        {selected: {addressInfo}, memo, pickup, virtualPhoneNumber: {used: usedVirtualPhoneNumber}},
        {
            selected: {
                duplicateCoupon,
                immediateCoupon,
                productCoupon,
                storeCoupon,
                deliveryCoupon,
                subscriptionCoupon,
            },
            useAllCoupon,
        },
        {paymentMethodSeqNo},
        {policesByItemId},
        {
            orderAmount,
            deliveryFeeAmount,
            deliveryDiscountAmount,
            totalDiscountAmount,
            productDiscountAmountByDiscountType,
        },
        {hasArrivalGuaranteeProduct},
        {multiPurchaseInfo: {multiPurchaseDiscountSelectedOrderSheetItemIds}},
    ) => {
        if (!addressInfo) {
            return 'NO_SELECTED_ADDRESS'
        }

        const {totalAmount} = getShoppingSubscriptionOrderTotal({
            deliveryFeeAmount,
            deliveryDiscountAmount,
            orderAmount,
            totalDiscountAmount,
            productDiscountAmountByDiscountType,
            useAllCoupon: !!useAllCoupon,
        })

        const deliveryInstruction = (() => {
            if (!hasArrivalGuaranteeProduct || !pickup?.location?.type) {
                return {
                    pickupLocationType: null,
                    pickupLocationContent: null,
                    entryMethodType: null,
                    entryMethodContent: null,
                }
            }

            return {
                pickupLocationType: pickup.location.type,
                pickupLocationContent: pickup.location.content || null,
                // 출입방법은 수령위치가 '문 앞'인 경우에만 유효
                entryMethodType: pickup.location.type === PickUpLocation.FRONT_OF_DOOR ? pickup.entryMethod.type : null,
                entryMethodContent:
                    pickup.location.type === PickUpLocation.FRONT_OF_DOOR ? pickup.entryMethod.content || null : null,
            }
        })()

        return {
            agreements: {
                agreeSubscriptionPay: agreed,
            },
            coupon: {
                selectedProductCouponIdsByOrderSheetItemId: mapValueKeys(
                    (couponViews) => couponViews.map(({couponId}) => couponId),
                    productCoupon,
                ),
                selectedImmediateDiscountPolicyNosByOrderSheetItemId: mapValueKeys(
                    (couponViews) => couponViews.map(({policyNo}) => policyNo),
                    immediateCoupon,
                ),
                selectedProductDuplicateCouponIdsByOrderSheetItemId: reduceValueKeys<
                    Record<string, string>,
                    OrderSheetCouponState['selected']['duplicateCoupon']
                >(
                    (acc, couponViews, key) => {
                        const selectedCoupon = head(couponViews)

                        if (selectedCoupon) {
                            acc[String(key)] = selectedCoupon.couponId
                        }

                        return acc
                    },
                    {},
                    duplicateCoupon,
                ),
                selectedSubscriptionDiscountPolicesByOrderSheetItemId: mapValueKeys((policies, orderSheetItemId) => {
                    if (subscriptionCoupon?.includes(orderSheetItemId)) {
                        return policies.map(({benefitType, discountStartPeriod, discountUnitType, discountValue}) => {
                            return {
                                benefitType,
                                count: String(discountStartPeriod),
                                discountUnitType:
                                    discountUnitType === 'RATIO' ? DiscountPolicy.RATIO : DiscountPolicy.AMOUNT,
                                discountValue,
                            }
                        })
                    }
                    return []
                }, policesByItemId),
                selectedStoreCouponIdsByMerchantNo: useAllCoupon
                    ? reduceValueKeys<Record<string, string>, OrderSheetCouponState['selected']['storeCoupon']>(
                          (acc, couponView, key) => {
                              if (couponView) {
                                  acc[key] = couponView.couponId
                              }

                              return acc
                          },
                          {},
                          storeCoupon,
                      )
                    : {},
                selectedDeliveryFeeCouponIdsByDeliveryGroupKey: useAllCoupon
                    ? reduceValueKeys<Record<string, string>, OrderSheetCouponState['selected']['deliveryCoupon']>(
                          (acc, couponView, key) => {
                              if (couponView) {
                                  acc[key] = couponView.couponId
                              }

                              return acc
                          },
                          {},
                          deliveryCoupon,
                      )
                    : {},
                multiPurchaseDiscountSelectedOrderSheetItemIds,
            },
            deliveryAddress: {
                addressName: addressInfo.addressName,
                baseAddress: addressInfo.baseAddress,
                detailAddress: addressInfo.detailAddress,
                receiverName: addressInfo.receiverName,
                road: addressInfo.isRoadNameAddress,
                telNo1: addressInfo.telNo1,
                telNo2: addressInfo.telNo2,
                useVirtualPhoneNumber: usedVirtualPhoneNumber ?? null,
                zipCode: addressInfo.zipCode,
                deliveryMemo: memo.individuallyInput.value,
                deliveryMemoParticularInput: memo.isParticularInput,
                reuseMemo: memo.saveAsDefaultMemo,
                particularDeliveryMemos: memo.isParticularInput
                    ? mapValueKeys(({value}) => {
                          return value
                      }, memo.particularInputByItemId)
                    : null,
                addToAddressBooks: false,
                personalCustomsCode: null,
                personalCustomsCodeNeeded: false,
                saveAsPrimary: false,
                buildingManagementNo: addressInfo.buildingManagementNo,
                locationX: addressInfo.locationX,
                locationY: addressInfo.locationY,
                ...deliveryInstruction,
            },
            payment: {
                expectedPayAmount: totalAmount,
                subscriptionPayMeansNo: String(paymentMethodSeqNo),
                useAllCoupon: !!useAllCoupon,
            },
        }
    },
)
